import { z } from 'zod';
import { ValidationError } from './errors.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
/** The print services the router considers. Embroidery is chosen manually, not routed. */
export const PrintService = z.enum(['screen_print', 'dtf_press_and_press', 'dtg']);
export const RouteJobInputSchema = z.object({
    qty: z.number().int().positive(),
    colors: z.number().int().positive(),
    sides: z.enum(['single', 'double']),
});
export const RouteJobResultSchema = z
    .object({
    recommended: PrintService,
    alternatives: z.array(PrintService),
    reason: z.string(),
})
    .meta({
    id: 'RouteJobResult',
    description: 'Routing recommendation. `alternatives` lists equivalent services for borderline cases; empty when there is a single clear winner.',
});
/**
 * Port of v14 `calcRoute`: recommends the optimal print service for a job given
 * quantity, colour count, and sidedness. Follows the v14 decision tree verbatim.
 *
 * Does NOT price the job — use `quote*` functions for that.
 */
export function routeJob(rawInput, config) {
    const parsed = RouteJobInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const { qty, colors, sides } = parsed.data;
    const isDouble = sides === 'double';
    const t = (config ?? DEFAULT_PRICING_CONFIG_BODY).routing.thresholds;
    // 1. qty <= dtgMaxQty: DTG is the clear winner (no setup, full colour, no minimum).
    if (qty <= t.dtgMaxQty) {
        return {
            recommended: 'dtg',
            alternatives: [],
            reason: 'Small quantity — DTG has no setup fee and supports full colour.',
        };
    }
    // 2. qty < dtfPpMinForSp: below the screen-print minimum; DTF P&P is the play.
    if (qty < t.dtfPpMinForSp) {
        return {
            recommended: 'dtf_press_and_press',
            alternatives: [],
            reason: `Between DTG range and the ${t.dtfPpMinForSp}-piece screen-print minimum — DTF P&P is optimal.`,
        };
    }
    // 3. qty >= dtfPpMinForSp, single side, 1-3 colours: screen print is the clear winner.
    if (!isDouble && colors <= t.spSingleSideMaxColorsClear) {
        return {
            recommended: 'screen_print',
            alternatives: [],
            reason: `${t.dtfPpMinForSp}+ pieces, 1-${t.spSingleSideMaxColorsClear} colours, single side — screen print is the clear winner.`,
        };
    }
    // 4. qty >= dtfPpMinForSp, single side, more colors: both viable; SP for durability, DTF for simpler setup.
    if (!isDouble && colors > t.spSingleSideMaxColorsClear) {
        return {
            recommended: 'screen_print',
            alternatives: ['dtf_press_and_press'],
            reason: '4+ colours at volume — screen print keeps per-unit cost lowest, DTF P&P has simpler setup.',
        };
    }
    // 5. Double-sided, qty < doubleSidedVolumeBreak, 1-2 colours: present both; SP better feel, DTF saves $2-4/shirt.
    if (isDouble && qty < t.doubleSidedVolumeBreak && colors <= 2) {
        return {
            recommended: 'screen_print',
            alternatives: ['dtf_press_and_press'],
            reason: 'Both work. Screen print has better feel; DTF P&P saves ~$2-4 per shirt.',
        };
    }
    // 6. Double-sided, qty >= doubleSidedVolumeBreak: volume pricing makes screen print decisive.
    if (isDouble && qty >= t.doubleSidedVolumeBreak) {
        return {
            recommended: 'screen_print',
            alternatives: [],
            reason: `Volume pricing at ${t.doubleSidedVolumeBreak}+ double-sided — screen print is decisive.`,
        };
    }
    // 7. qty >= anyServiceVolumeBreak: screen print wins any configuration.
    if (qty >= t.anyServiceVolumeBreak) {
        return {
            recommended: 'screen_print',
            alternatives: [],
            reason: `${t.anyServiceVolumeBreak}+ pieces — screen print is unbeatable on cost for any configuration.`,
        };
    }
    // 8. Fallback (v14 `calcRoute` default branch): DTF P&P.
    return {
        recommended: 'dtf_press_and_press',
        alternatives: [],
        reason: 'Configuration outside screen-print sweet spot — DTF P&P is the safer default.',
    };
}
//# sourceMappingURL=route.js.map